/**
 * CompanySheet — Right-side detail sheet for a company
 *
 * Ported from EBTimeEntry for the new table-based integrations UI.
 *
 * Sections:
 *   1. Company Details (name, code, status toggle)
 *   2. Integrations (Financial, HR, Project, Planner config + test/sync)
 *   3. Users with Access (list + add/remove)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Building2,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  Plus,
  X,
  UserRound,
  Search,
  PoundSterling,
  Users,
  FolderKanban,
  CalendarDays,
  Clock,
  ChevronDown,
  RefreshCw,
  Trash2,
  UserMinus,
  UserPlus,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import * as integrationsService from '@/services/integrationsService';
import { mockIntegrationsService } from '@/data/mockIntegrationsData';
import type { ExternalInstanceDto, AccessProjectsCompanyDto } from '@/services/api';
import {
  FINANCIAL_PRODUCT_LABELS,
  HR_PRODUCT_LABELS,
  PROJECT_PRODUCT_LABELS,
  PLANNER_PRODUCT_LABELS,
  TIMESHEET_PRODUCT_LABELS,
} from '@/types/integrations';
import type {
  CompanyWithIntegrations,
  CompanyUser,
  FinancialProduct,
  HRProduct,
  ProjectProduct,
  PlannerProduct,
  TimesheetProduct,
  IntegrationConnectionStatus,
  UpdateIntegrationsPayload,
} from '@/types/integrations';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CompanySheetProps {
  company: CompanyWithIntegrations | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompanyUpdate: (updated: CompanyWithIntegrations) => void;
  mode?: 'view' | 'create';
  onCompanyCreated?: (created: CompanyWithIntegrations) => void;
  existingCodes?: string[];
  isDemo?: boolean;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function formatLastSync(lastSyncAt: string | null): string {
  if (!lastSyncAt) return 'Never';
  return new Date(lastSyncAt).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function RoleBadge({ role }: { role: string }) {
  const lower = role.toLowerCase();
  let className = 'border-muted-foreground/30 text-muted-foreground';

  if (lower.includes('admin') || lower.includes('manager') || lower.includes('director')) {
    className = 'border-blue-400 text-blue-700 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-300';
  } else if (lower.includes('lead') || lower.includes('senior')) {
    className = 'border-violet-400 text-violet-700 bg-violet-50 dark:bg-violet-950/30 dark:text-violet-300';
  } else if (lower.includes('finance') || lower.includes('account')) {
    className = 'border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300';
  } else if (lower.includes('hr') || lower.includes('people')) {
    className = 'border-pink-400 text-pink-700 bg-pink-50 dark:bg-pink-950/30 dark:text-pink-300';
  }

  return (
    <Badge variant="outline" className={cn('text-[10px] font-medium px-1.5 py-0 shrink-0', className)}>
      {role}
    </Badge>
  );
}

function ConnectionStatusBadge({ status }: { status: IntegrationConnectionStatus }) {
  switch (status) {
    case 'connected':
      return (
        <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50 gap-1 text-xs">
          <CheckCircle2 className="h-3 w-3" />
          Connected
        </Badge>
      );
    case 'error':
      return (
        <Badge variant="destructive" className="gap-1 text-xs">
          <AlertCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    case 'not_connected':
      return (
        <Badge variant="secondary" className="gap-1 text-xs">
          <XCircle className="h-3 w-3" />
          Not Connected
        </Badge>
      );
    case 'not_configured':
      return (
        <Badge variant="outline" className="text-muted-foreground gap-1 text-xs">
          Not Configured
        </Badge>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Section 1 — Company Details
// ---------------------------------------------------------------------------

interface CompanyDetailsSectionProps {
  company: CompanyWithIntegrations;
  onUpdate: (updated: CompanyWithIntegrations) => void;
}

function CompanyDetailsSection({ company, onUpdate }: CompanyDetailsSectionProps) {
  const [name, setName] = useState(company.name);
  const [code, setCode] = useState(company.code);
  const [isActive, setIsActive] = useState(company.status === 'active');
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setName(company.name);
    setCode(company.code);
    setIsActive(company.status === 'active');
    setIsDirty(false);
  }, [company.id]);

  function markDirty() {
    setIsDirty(true);
  }

  async function handleSave() {
    setIsSaving(true);
    try {
      const updated = await integrationsService.updateCompany(company.id, {
        name,
        code: code.toUpperCase(),
        status: isActive ? 'active' : 'inactive',
      });
      if (updated) {
        onUpdate(updated);
        setIsDirty(false);
        toast.success('Company details saved');
      }
    } catch {
      toast.error('Failed to save company details');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="company-name">Company Name</Label>
          <Input
            id="company-name"
            value={name}
            onChange={(e) => { setName(e.target.value); markDirty(); }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="company-code">Company Code</Label>
          <Input
            id="company-code"
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); markDirty(); }}
            placeholder="e.g. ACME"
            className="uppercase"
          />
        </div>
        <div className="flex items-center justify-between py-1">
          <div>
            <Label htmlFor="company-status" className="text-sm font-medium">Active</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Inactive companies are hidden from the company switcher
            </p>
          </div>
          <Switch
            id="company-status"
            checked={isActive}
            onCheckedChange={(checked) => { setIsActive(checked); markDirty(); }}
          />
        </div>
      </div>

      {isDirty && (
        <Button size="sm" onClick={handleSave} disabled={isSaving} className="w-full">
          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
          Save Details
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 2 — Integration sub-form
// ---------------------------------------------------------------------------

interface IntegrationSubSectionProps {
  companyId: string;
  type: 'financial' | 'hr' | 'project' | 'planner' | 'timesheet';
  product: FinancialProduct | HRProduct | ProjectProduct | PlannerProduct | TimesheetProduct | null;
  externalInstanceId: string | null;
  /** Secondary external instance ID (e.g. EBProjects numeric CompanyId for access-projects) */
  externalInstanceIdSecondary?: string | null;
  status: IntegrationConnectionStatus;
  lastSyncAt: string | null;
  onSaved: (updated: CompanyWithIntegrations) => void;
  /** When true, all edit controls (save, test, sync, remove) are hidden. */
  readOnly?: boolean;
}

function IntegrationSubSection({
  companyId,
  type,
  product,
  externalInstanceId,
  externalInstanceIdSecondary,
  status,
  lastSyncAt,
  onSaved,
  readOnly = false,
}: IntegrationSubSectionProps) {
  const [selectedProduct, setSelectedProduct] = useState<string>(product ?? '');
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>(externalInstanceId ?? '');
  const [selectedInstanceName, setSelectedInstanceName] = useState<string>('');
  const [externalInstances, setExternalInstances] = useState<ExternalInstanceDto[]>([]);
  const [isLoadingInstances, setIsLoadingInstances] = useState(false);
  const [noInstancesFound, setNoInstancesFound] = useState(false);
  // Access Projects companies (second API call to resolve company names)
  const [accessProjectsCompanies, setAccessProjectsCompanies] = useState<AccessProjectsCompanyDto[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(externalInstanceIdSecondary ?? '');
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [noCompaniesFound, setNoCompaniesFound] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [localLastSyncAt, setLocalLastSyncAt] = useState<string | null>(lastSyncAt);
  const initialLoadDone = useRef(false);

  // Sync local state when the parent passes new props (company or integration changed).
  // Also load the instance list so the saved externalInstanceId can be preserved.
  useEffect(() => {
    setSelectedProduct(product ?? '');
    setSelectedInstanceId(externalInstanceId ?? '');
    setSelectedInstanceName('');
    setLocalLastSyncAt(lastSyncAt);
    setIsDirty(false);
    initialLoadDone.current = false;
    setSelectedCompanyId(externalInstanceIdSecondary ?? '');
    setAccessProjectsCompanies([]);
    setNoCompaniesFound(false);

    if (product) {
      // For access-projects, skip the external-instances API call
      // and use the companies endpoint directly (same as EBProjects).
      if (product === 'access-projects') {
        loadCompanies(product, externalInstanceId ?? undefined);
      } else {
        loadInstances(product, externalInstanceId ?? undefined);
      }
    } else {
      setExternalInstances([]);
      setNoInstancesFound(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, externalInstanceId, externalInstanceIdSecondary, lastSyncAt, companyId]);

  /**
   * Fetch the available external instances for a product code.
   * When `preselectId` is supplied (editing an existing integration) the
   * dropdown will keep the saved instance selected instead of auto-picking
   * the first entry.  This mirrors EBTimeEntry's loadInstances pattern.
   */
  async function loadInstances(productCode: string, preselectId?: string) {
    setIsLoadingInstances(true);
    setNoInstancesFound(false);
    setExternalInstances([]);
    try {
      const instances = await integrationsService.getExternalInstances(productCode);
      setExternalInstances(instances);
      if (instances.length === 0) {
        setNoInstancesFound(true);
        setSelectedInstanceId('');
        setSelectedInstanceName('');
      } else if (preselectId) {
        // Preserve the previously-saved instance when it still exists
        const match = instances.find((i) => i.id === preselectId);
        if (match) {
          setSelectedInstanceName(match.name);
        } else {
          // Saved instance gone — fall back to first
          setSelectedInstanceName(instances[0].name);
          setSelectedInstanceId(instances[0].id);
        }
      } else {
        // New integration — auto-select first instance
        setSelectedInstanceName(instances[0].name);
        setSelectedInstanceId(instances[0].id);
      }
      // Load the company dropdown via the backend databases API.
      // For access-projects: call the companies API (secondary lookup).
      // For access-time: call /api/integrations/databases?product=access-time
      //   (backend calls Access Time API, falls back to Workspace instances).
      // For other products: the Workspace instances themselves ARE the companies.
      // (same pattern as EBProjects GET /api/integrations/databases?product=...)
      if (instances.length > 0) {
        if (productCode === 'access-projects') {
          loadCompanies(productCode);
        } else if (productCode === 'access-time') {
          loadDatabases(productCode);
        } else {
          setAccessProjectsCompanies(
            instances.map((inst) => ({ id: inst.id, name: inst.name }))
          );
        }
      }
    } catch {
      setNoInstancesFound(true);
      setSelectedInstanceId('');
      setSelectedInstanceName('');
    } finally {
      setIsLoadingInstances(false);
      initialLoadDone.current = true;
    }
  }

  async function loadCompanies(productCode: string, preselectId?: string) {
    setIsLoadingCompanies(true);
    setNoCompaniesFound(false);
    setAccessProjectsCompanies([]);
    try {
      // Call without instanceId — backend auto-discovers instances via Workspace API
      const companies = await integrationsService.getAccessProjectsCompanies({ product: productCode });
      setAccessProjectsCompanies(companies);
      if (companies.length === 0) {
        setNoCompaniesFound(true);
      } else if (preselectId) {
        const match = companies.find((c) => c.id === preselectId);
        if (match) {
          setSelectedInstanceName(match.name ?? match.id);
          setSelectedInstanceId(match.id);
          setSelectedCompanyId(match.id);
        } else {
          setSelectedInstanceName(companies[0].name ?? companies[0].id);
          setSelectedInstanceId(companies[0].id);
          setSelectedCompanyId(companies[0].id);
        }
      } else {
        setSelectedInstanceName(companies[0].name ?? companies[0].id);
        setSelectedInstanceId(companies[0].id);
        setSelectedCompanyId(companies[0].id);
      }
    } catch {
      setNoCompaniesFound(true);
    } finally {
      setIsLoadingCompanies(false);
      initialLoadDone.current = true;
    }
  }

  /**
   * Load databases from /api/integrations/databases?product=...
   * Same pattern as EBProjects GET /api/integrations/databases.
   * Maps AvailableDatabaseDto (id, label) to the company dropdown shape.
   */
  async function loadDatabases(productCode: string, preselectId?: string) {
    setIsLoadingCompanies(true);
    setNoCompaniesFound(false);
    setAccessProjectsCompanies([]);
    try {
      const databases = await integrationsService.getAvailableDatabases(productCode);
      const mapped = databases.map((db) => ({ id: db.id, name: db.label }));
      setAccessProjectsCompanies(mapped);
      if (mapped.length === 0) {
        setNoCompaniesFound(true);
      } else if (preselectId) {
        const match = mapped.find((c) => c.id === preselectId);
        if (match) {
          setSelectedInstanceName(match.name);
          setSelectedInstanceId(match.id);
        } else {
          setSelectedInstanceName(mapped[0].name);
          setSelectedInstanceId(mapped[0].id);
        }
      } else {
        setSelectedInstanceName(mapped[0].name);
        setSelectedInstanceId(mapped[0].id);
      }
    } catch {
      setNoCompaniesFound(true);
    } finally {
      setIsLoadingCompanies(false);
      initialLoadDone.current = true;
    }
  }

  function handleProductChange(value: string) {
    setSelectedProduct(value);
    setIsDirty(true);
    setSelectedInstanceId('');
    setSelectedInstanceName('');
    setExternalInstances([]);
    setNoInstancesFound(false);
    setAccessProjectsCompanies([]);
    setSelectedCompanyId('');
    setNoCompaniesFound(false);
    if (value) {
      // For access-projects and access-time, skip the external-instances API call
      // and use the databases/companies endpoint directly.
      if (value === 'access-projects') {
        loadCompanies(value);
      } else if (value === 'access-time') {
        loadDatabases(value);
      } else {
        loadInstances(value);
      }
    }
  }

  function handleInstanceChange(instanceName: string) {
    setSelectedInstanceName(instanceName);
    // For access-projects / access-time the dropdown is backed by
    // accessProjectsCompanies (not externalInstances).
    const fromCompanies = accessProjectsCompanies.find(
      (c) => (c.name ?? c.id) === instanceName
    );
    const fromInstances = externalInstances.find((i) => i.name === instanceName);
    setSelectedInstanceId(fromCompanies?.id ?? fromInstances?.id ?? '');
    // Keep selectedCompanyId in sync for externalInstanceIdSecondary (access-projects)
    if (fromCompanies) {
      setSelectedCompanyId(fromCompanies.id);
    }
    setIsDirty(true);
  }

  const productOptions =
    type === 'financial'
      ? (Object.entries(FINANCIAL_PRODUCT_LABELS) as [FinancialProduct, string][])
      : type === 'hr'
        ? (Object.entries(HR_PRODUCT_LABELS) as [HRProduct, string][])
        : type === 'project'
          ? (Object.entries(PROJECT_PRODUCT_LABELS) as [ProjectProduct, string][])
          : type === 'planner'
            ? (Object.entries(PLANNER_PRODUCT_LABELS) as [PlannerProduct, string][])
            : (Object.entries(TIMESHEET_PRODUCT_LABELS) as [TimesheetProduct, string][]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const emptyField = { product: null, externalInstanceId: null };
      const isAccessProjects = selectedProduct === 'access-projects';
      const activeField = {
        product: (selectedProduct || null) as FinancialProduct | HRProduct | ProjectProduct | PlannerProduct | null,
        externalInstanceId: selectedInstanceId || null,
        instanceName: selectedInstanceName || null,
        ...(type === 'project' && isAccessProjects ? { externalInstanceIdSecondary: selectedCompanyId || null } : {}),
      };
      const payload: UpdateIntegrationsPayload = {
        financial: (type === 'financial' ? activeField : emptyField) as UpdateIntegrationsPayload['financial'],
        hr: (type === 'hr' ? activeField : emptyField) as UpdateIntegrationsPayload['hr'],
        project: (type === 'project' ? activeField : emptyField) as UpdateIntegrationsPayload['project'],
        planner: (type === 'planner' ? activeField : emptyField) as UpdateIntegrationsPayload['planner'],
        timesheet: (type === 'timesheet' ? activeField : emptyField) as UpdateIntegrationsPayload['timesheet'],
      };
      const updated = await integrationsService.updateIntegrations(companyId, payload);
      if (updated) {
        onSaved(updated);
        setIsDirty(false);
        toast.success(
          `${type === 'financial' ? 'Financial' : type === 'hr' ? 'HR' : type === 'project' ? 'Project' : type === 'planner' ? 'Planner' : 'Timesheet'} integration saved`
        );
      }
    } catch {
      toast.error('Failed to save integration');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleTest() {
    setIsTesting(true);
    try {
      const result = await integrationsService.testConnection(companyId, type);
      if (result.success) {
        toast.success('Connection successful', { description: result.message });
      } else {
        toast.error('Connection failed', { description: result.message });
      }
    } catch {
      toast.error('Test connection failed');
    } finally {
      setIsTesting(false);
    }
  }

  async function handleSync() {
    setIsSyncing(true);
    try {
      const result = await integrationsService.syncIntegration(companyId, type);
      if (result.success) {
        if (result.syncedAt) setLocalLastSyncAt(result.syncedAt);
        if (result.updatedCompany) onSaved(result.updatedCompany);
        toast.success('Sync complete', { description: result.message });
      } else {
        toast.error('Sync failed', { description: result.message });
      }
    } catch {
      toast.error('Sync failed — please try again');
    } finally {
      setIsSyncing(false);
    }
  }

  async function handleRemove() {
    setIsRemoving(true);
    try {
      const updated = await integrationsService.removeIntegration(companyId, type);
      if (updated) {
        onSaved(updated);
        setSelectedProduct('');
        setSelectedInstanceId('');
        setSelectedInstanceName('');
        setExternalInstances([]);
        setLocalLastSyncAt(null);
        setIsDirty(false);
        toast.success(
          `${type === 'financial' ? 'Financial' : type === 'hr' ? 'HR' : type === 'project' ? 'Project' : type === 'planner' ? 'Planner' : 'Timesheet'} integration removed`
        );
      }
    } catch {
      toast.error('Failed to remove integration');
    } finally {
      setIsRemoving(false);
      setShowRemoveConfirm(false);
    }
  }

  const label =
    type === 'financial'
      ? 'Financial Integration'
      : type === 'hr'
        ? 'HR Integration'
        : type === 'project'
          ? 'Project Integration'
          : type === 'planner'
            ? 'Planner Integration'
            : 'Timesheet Integration';

  const icon =
    type === 'financial' ? (
      <PoundSterling className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : type === 'hr' ? (
      <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : type === 'project' ? (
      <FolderKanban className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : type === 'planner' ? (
      <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : (
      <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    );

  const defaultOpen = status !== 'not_configured';

  return (
    <>
    <Collapsible defaultOpen={defaultOpen}>
      <div className="flex items-center gap-1 rounded-md hover:bg-muted/50 transition-colors group">
        <CollapsibleTrigger className="flex-1 flex items-center gap-2 px-1 py-1.5 min-w-0">
          {icon}
          <span className="text-sm font-semibold flex-1 text-left truncate">{label}</span>
          <ConnectionStatusBadge status={status} />
          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 flex-shrink-0 ml-1" />
        </CollapsibleTrigger>
        {status !== 'not_configured' && !readOnly && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={(e) => { e.stopPropagation(); setShowRemoveConfirm(true); }}
            disabled={isRemoving}
            title="Remove integration"
          >
            {isRemoving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>

      <CollapsibleContent>
        <div className="space-y-2 pl-6 pt-3">
          {/* Product dropdown */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Product</Label>
            {readOnly ? (
              <p className="text-sm py-1">
                {selectedProduct
                  ? (productOptions.find(([v]) => v === selectedProduct)?.[1] ?? selectedProduct)
                  : <span className="text-muted-foreground italic">Not configured</span>}
              </p>
            ) : (
              <Select
                value={selectedProduct}
                onValueChange={handleProductChange}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select product..." />
                </SelectTrigger>
                <SelectContent>
                  {productOptions.map(([value, optLabel]) => (
                    <SelectItem key={value} value={value}>
                      {optLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Instance dropdown from Workspace API (products other than access-projects and access-time) */}
          {selectedProduct && selectedProduct !== 'access-projects' && selectedProduct !== 'access-time' && (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Instance Name</Label>
            {readOnly ? (
              <p className="text-sm py-1">
                {selectedInstanceName
                  ? selectedInstanceName
                  : <span className="text-muted-foreground italic">&mdash;</span>}
              </p>
            ) : isLoadingInstances ? (
              <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading instances...
              </div>
            ) : noInstancesFound ? (
              <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2">
                <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                <span className="text-xs text-destructive">
                  No Instances Found. Unable to configure this product.
                </span>
              </div>
            ) : externalInstances.length > 0 ? (
              <>
                <Select value={selectedInstanceName} onValueChange={handleInstanceChange}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select an instance name" />
                  </SelectTrigger>
                  <SelectContent>
                    {externalInstances.map((instance) => (
                      <SelectItem key={instance.id} value={instance.name}>
                        {instance.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Select an external instance for this product
                </p>
              </>
            ) : null}
          </div>
          )}

          {/* Company dropdown for Access Projects / Access Time (resolved via backend API) */}
          {(selectedProduct === 'access-projects' || selectedProduct === 'access-time') && (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Company</Label>
            {readOnly ? (
              <p className="text-sm py-1">
                {selectedInstanceName
                  ? selectedInstanceName
                  : <span className="text-muted-foreground italic">&mdash;</span>}
              </p>
            ) : isLoadingInstances || isLoadingCompanies ? (
              <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading companies...
              </div>
            ) : noInstancesFound || noCompaniesFound ? (
              <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2">
                <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                <span className="text-xs text-destructive">
                  No Companies Found. Unable to configure this product.
                </span>
              </div>
            ) : accessProjectsCompanies.length > 0 ? (
              <>
                <Select value={selectedInstanceName} onValueChange={handleInstanceChange}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select a company" />
                  </SelectTrigger>
                  <SelectContent>
                    {accessProjectsCompanies.map((company) => (
                      <SelectItem key={company.id} value={company.name ?? company.id}>
                        {company.name ?? company.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Select the company for this project integration
                </p>
              </>
            ) : null}
          </div>
          )}

          {/* Last sync */}
          {localLastSyncAt && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3 flex-shrink-0" />
              <span>Last synced: <span className="font-medium text-foreground">{formatLastSync(localLastSyncAt)}</span></span>
            </div>
          )}

          {/* Actions — hidden in read-only mode */}
          {!readOnly && <div className="flex flex-wrap gap-2 pt-1 pb-2">
            {/* Sync Now — only when connected */}
            {status === 'connected' && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7"
                onClick={handleSync}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                {isSyncing ? 'Syncing…' : 'Sync Now'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={handleTest}
              disabled={isTesting || !selectedProduct || !selectedInstanceId}
            >
              {isTesting ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3 w-3 mr-1" />
              )}
              Test Connection
            </Button>
            <Button
              size="sm"
              className="text-xs h-7"
              onClick={handleSave}
              disabled={isSaving || !isDirty}
            >
              {isSaving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Save
            </Button>
          </div>}
        </div>
      </CollapsibleContent>
    </Collapsible>

    {/* Remove Integration confirmation */}
    <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {label}?</AlertDialogTitle>
          <AlertDialogDescription>
            This will disconnect and remove all configuration for the {label.toLowerCase()}.
            Any active sync schedule will be cancelled. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleRemove}
            disabled={isRemoving}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isRemoving ? (
              <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Removing...</>
            ) : (
              'Remove Integration'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Add Users Dialog — browsable multi-select modal
// ---------------------------------------------------------------------------

interface AddUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanyWithIntegrations;
  onUsersAdded: () => void;
}

function AddUsersDialog({ open, onOpenChange, company, onUsersAdded }: AddUsersDialogProps) {
  const [allUsers, setAllUsers] = useState<CompanyUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [dialogSearch, setDialogSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDialogSearch('');
    setSelectedIds(new Set());
    setIsLoadingUsers(true);
    integrationsService.fetchAllUsers(company.id)
      .then(setAllUsers)
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setIsLoadingUsers(false));
  }, [open, company.id]);

  const filteredDialogUsers = dialogSearch.trim()
    ? allUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(dialogSearch.toLowerCase()) ||
          u.email.toLowerCase().includes(dialogSearch.toLowerCase()) ||
          u.role.toLowerCase().includes(dialogSearch.toLowerCase())
      )
    : allUsers;

  const allFilteredSelected =
    filteredDialogUsers.length > 0 &&
    filteredDialogUsers.every((u) => selectedIds.has(u.id));
  const someFilteredSelected =
    filteredDialogUsers.some((u) => selectedIds.has(u.id)) && !allFilteredSelected;

  function toggleUser(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllFiltered() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredDialogUsers.forEach((u) => next.delete(u.id));
      } else {
        filteredDialogUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  }

  async function handleConfirmAdd() {
    if (selectedIds.size === 0) return;
    setIsAdding(true);
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map((id) => integrationsService.addUserToCompany(company.id, id)));
      const count = ids.length;
      toast.success(`${count} user${count !== 1 ? 's' : ''} added to ${company.name}`);
      onUsersAdded();
      onOpenChange(false);
    } catch {
      toast.error('Failed to add some users');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden flex flex-col max-h-[85vh]">
        <DialogHeader className="px-5 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserPlus className="h-4 w-4 text-muted-foreground" />
            Add Users to {company.name}
          </DialogTitle>
          <DialogDescription className="text-xs mt-1">
            Select one or more users to grant access to this company.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="px-4 py-3 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder="Search by name, email, role\u2026"
              value={dialogSearch}
              onChange={(e) => setDialogSearch(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Select all header */}
        {!isLoadingUsers && filteredDialogUsers.length > 0 && (
          <div
            className="flex items-center gap-3 px-4 py-2 bg-muted/40 border-b shrink-0 cursor-pointer hover:bg-muted/60 transition-colors"
            onClick={toggleAllFiltered}
          >
            <Checkbox
              checked={allFilteredSelected}
              data-state={someFilteredSelected ? 'indeterminate' : undefined}
              onCheckedChange={toggleAllFiltered}
              className="pointer-events-none"
            />
            <span className="text-xs font-medium text-muted-foreground">
              {allFilteredSelected
                ? 'Deselect all'
                : someFilteredSelected
                ? `${selectedIds.size} selected \u2014 select all ${filteredDialogUsers.length}`
                : `Select all ${filteredDialogUsers.length} user${filteredDialogUsers.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        )}

        {/* User list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoadingUsers ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 py-1">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="space-y-1 flex-1">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredDialogUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center gap-2 px-4">
              {dialogSearch ? (
                <>
                  <Search className="h-7 w-7 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No users match "{dialogSearch}"</p>
                </>
              ) : (
                <>
                  <Users className="h-7 w-7 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">All users already have access</p>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filteredDialogUsers.map((user) => {
                const isSelected = selectedIds.has(user.id);
                return (
                  <div
                    key={user.id}
                    className={cn(
                      'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors',
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'
                    )}
                    onClick={() => toggleUser(user.id)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleUser(user.id)}
                      className="pointer-events-none shrink-0"
                    />
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium flex-shrink-0">
                      {getInitials(user.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-tight truncate">{user.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">{user.role}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-4 py-3 border-t shrink-0 gap-2 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isAdding}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirmAdd}
            disabled={selectedIds.size === 0 || isAdding}
            className="gap-1.5"
          >
            {isAdding ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <UserPlus className="h-3.5 w-3.5" />
            )}
            {selectedIds.size > 0
              ? `Add ${selectedIds.size} User${selectedIds.size !== 1 ? 's' : ''}`
              : 'Add Users'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Bulk Remove Bar — floats at bottom of sheet when users are selected
// ---------------------------------------------------------------------------

interface BulkRemoveBarProps {
  selectedCount: number;
  onRemove: () => void;
  onClear: () => void;
  isRemoving: boolean;
}

function BulkRemoveBar({ selectedCount, onRemove, onClear, isRemoving }: BulkRemoveBarProps) {
  if (selectedCount === 0) return null;
  return (
    <div className="sticky bottom-0 left-0 right-0 z-10 pointer-events-none">
      <div className="flex justify-center pb-2">
        <div className="pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-full bg-[#1a7f6e] text-white shadow-2xl text-sm whitespace-nowrap animate-in slide-in-from-bottom-4 duration-300">
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white/25 text-xs font-bold px-1">
            {selectedCount}
          </span>
          <span className="font-medium pr-1">
            {selectedCount === 1 ? 'user' : 'users'} selected
          </span>
          <div className="h-4 w-px bg-white/30" />
          <button
            onClick={onRemove}
            disabled={isRemoving}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors font-medium disabled:opacity-60"
          >
            {isRemoving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <UserMinus className="h-3.5 w-3.5" />
            )}
            Remove Access
          </button>
          <div className="h-4 w-px bg-white/30" />
          <button
            onClick={onClear}
            className="flex items-center gap-1 text-white/80 hover:text-white transition-colors px-1"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
            <span className="text-xs">Clear</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 3 — Users with Access
// ---------------------------------------------------------------------------

interface UsersWithAccessSectionProps {
  company: CompanyWithIntegrations;
  onUserCountChange: (count: number) => void;
}

function UsersWithAccessSection({ company, onUserCountChange }: UsersWithAccessSectionProps) {
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterQuery, setFilterQuery] = useState('');

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Add Users dialog
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // Bulk remove state
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await integrationsService.getCompanyUsers(company.id);
      setUsers(data);
      onUserCountChange(data.length);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id]);

  useEffect(() => {
    loadUsers();
    setSelectedIds(new Set());
  }, [loadUsers]);

  const filteredUsers = filterQuery.trim()
    ? users.filter(
        (u) =>
          u.name.toLowerCase().includes(filterQuery.toLowerCase()) ||
          u.role.toLowerCase().includes(filterQuery.toLowerCase())
      )
    : users;

  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.has(u.id));
  const someFilteredSelected =
    filteredUsers.some((u) => selectedIds.has(u.id)) && !allFilteredSelected;

  function toggleUser(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllFiltered() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredUsers.forEach((u) => next.delete(u.id));
      } else {
        filteredUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  }

  async function handleBulkRemove() {
    setIsBulkRemoving(true);
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(
        ids.map((id) => integrationsService.removeUserFromCompany(company.id, id))
      );
      const count = ids.length;
      toast.success(
        `Removed ${count} user${count !== 1 ? 's' : ''} from ${company.name}`
      );
      setSelectedIds(new Set());
      setShowBulkConfirm(false);
      await loadUsers();
    } catch {
      toast.error('Failed to remove some users');
    } finally {
      setIsBulkRemoving(false);
    }
  }

  const selectedCount = selectedIds.size;

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar: filter search + Add Users button */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-8 h-8 text-sm"
            placeholder="Filter users\u2026"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1 shrink-0"
          onClick={() => setIsAddDialogOpen(true)}
        >
          <UserPlus className="h-3 w-3" />
          Add Users
        </Button>
      </div>

      {/* Select-all header row */}
      {!isLoading && filteredUsers.length > 0 && (
        <div
          className="flex items-center gap-3 px-2 py-1.5 rounded-md bg-muted/40 border cursor-pointer hover:bg-muted/60 transition-colors"
          onClick={toggleAllFiltered}
        >
          <Checkbox
            checked={allFilteredSelected}
            data-state={someFilteredSelected ? 'indeterminate' : undefined}
            onCheckedChange={toggleAllFiltered}
            className="pointer-events-none"
          />
          <span className="text-xs text-muted-foreground font-medium flex-1">
            {allFilteredSelected
              ? 'Deselect all'
              : someFilteredSelected
              ? `${selectedCount} of ${filteredUsers.length} selected`
              : filterQuery.trim()
              ? `Select all ${filteredUsers.length} matching`
              : `Select all ${filteredUsers.length} users`}
          </span>
          {!isLoading && (
            <span className="text-xs text-muted-foreground">
              {filterQuery.trim()
                ? `${filteredUsers.length} of ${users.length}`
                : `${users.length} user${users.length !== 1 ? 's' : ''}`}
            </span>
          )}
        </div>
      )}

      {/* User list */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 py-1 px-2">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1 flex-1">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
          <UserRound className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No users assigned yet.</p>
          <p className="text-xs text-muted-foreground">
            Use "Add Users" to grant access to this company.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-1 gap-1.5 text-xs"
            onClick={() => setIsAddDialogOpen(true)}
          >
            <UserPlus className="h-3.5 w-3.5" />
            Add Users
          </Button>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center gap-1">
          <Search className="h-6 w-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No users match "{filterQuery}"</p>
        </div>
      ) : (
        <div className="divide-y rounded-md border overflow-hidden">
          {filteredUsers.map((user) => {
            const isSelected = selectedIds.has(user.id);
            return (
              <div
                key={user.id}
                className={cn(
                  'flex items-center gap-3 py-2.5 px-3 transition-colors cursor-pointer group',
                  isSelected ? 'bg-primary/5' : 'hover:bg-muted/40'
                )}
                onClick={() => toggleUser(user.id)}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleUser(user.id)}
                  className="pointer-events-none shrink-0"
                />
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium flex-shrink-0">
                  {getInitials(user.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium leading-tight truncate">{user.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user.email ?? user.role}</div>
                </div>
                <RoleBadge role={user.role} />
              </div>
            );
          })}
        </div>
      )}

      {/* Read-only note */}
      {!isLoading && (
        <div className="flex items-center justify-between pt-1 border-t gap-2">
          <p className="text-xs text-muted-foreground">
            To manage a user's full profile and company access, go to Team Members.
          </p>
        </div>
      )}

      {/* Bulk Remove floating bar */}
      <BulkRemoveBar
        selectedCount={selectedCount}
        onRemove={() => setShowBulkConfirm(true)}
        onClear={() => setSelectedIds(new Set())}
        isRemoving={isBulkRemoving}
      />

      {/* Add Users dialog */}
      <AddUsersDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        company={company}
        onUsersAdded={loadUsers}
      />

      {/* Bulk remove confirmation */}
      <AlertDialog open={showBulkConfirm} onOpenChange={(o) => { if (!o) setShowBulkConfirm(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {selectedCount} user{selectedCount !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedCount === 1
                ? 'This user will lose access to '
                : `These ${selectedCount} users will lose access to `}
              <strong>{company.name}</strong>. They will no longer see this company in their
              switcher.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkRemove}
              disabled={isBulkRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isBulkRemoving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Company Form
// ---------------------------------------------------------------------------

interface CreateCompanyFormProps {
  existingCodes: string[];
  onCreated: (company: CompanyWithIntegrations) => void;
  onCancel: () => void;
  isDemo?: boolean;
}

function CreateCompanyForm({ existingCodes, onCreated, onCancel, isDemo }: CreateCompanyFormProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; code?: string }>({});

  function validate() {
    const e: { name?: string; code?: string } = {};
    if (!name.trim() || name.trim().length < 2) e.name = 'Company name must be at least 2 characters';
    if (!code.trim() || code.trim().length < 2) e.code = 'Company code must be at least 2 characters';
    else if (code.trim().length > 20) e.code = 'Company code must be at most 20 characters';
    else if (!/^[A-Z0-9-]+$/.test(code.trim())) e.code = 'Uppercase letters, numbers, and hyphens only';
    else if (existingCodes.includes(code.trim().toUpperCase())) e.code = 'This company code is already in use';
    return e;
  }

  async function handleSave() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setIsSaving(true);
    try {
      const created = isDemo
        ? await mockIntegrationsService.createCompany({ name: name.trim(), code: code.trim().toUpperCase(), status })
        : await integrationsService.createCompany({ name: name.trim(), code: code.trim().toUpperCase(), status });
      toast.success(`${created.name} created`);
      onCreated(created);
    } catch {
      toast.error('Failed to create company');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="px-6 py-5 space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="new-company-name">Company Name <span className="text-destructive">*</span></Label>
        <Input
          id="new-company-name"
          value={name}
          placeholder="e.g., UK Operations"
          onChange={(e) => { setName(e.target.value); setErrors((prev) => ({ ...prev, name: undefined })); }}
          disabled={isSaving}
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="new-company-code">Company Code <span className="text-destructive">*</span></Label>
        <Input
          id="new-company-code"
          value={code}
          placeholder="e.g., UK-OPS"
          className="uppercase"
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setErrors((prev) => ({ ...prev, code: undefined })); }}
          disabled={isSaving}
        />
        <p className="text-xs text-muted-foreground">Unique identifier — uppercase letters, numbers and hyphens only</p>
        {errors.code && <p className="text-xs text-destructive">{errors.code}</p>}
      </div>

      <div className="flex items-center justify-between py-1">
        <div>
          <Label htmlFor="new-company-status" className="text-sm font-medium">Active</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Inactive companies are hidden from the company switcher</p>
        </div>
        <Switch
          id="new-company-status"
          checked={status === 'active'}
          onCheckedChange={(checked) => setStatus(checked ? 'active' : 'inactive')}
          disabled={isSaving}
        />
      </div>

      <Separator />

      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button className="flex-1" onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
          Create Company
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main CompanySheet
// ---------------------------------------------------------------------------

export function CompanySheet({
  company,
  open,
  onOpenChange,
  onCompanyUpdate,
  mode = 'view',
  onCompanyCreated,
  existingCodes = [],
  isDemo = false,
}: CompanySheetProps) {
  function handleUserCountChange(count: number) {
    if (!company) return;
    onCompanyUpdate({ ...company, userCount: count });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col overflow-hidden">
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
              <Building2 className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="text-base leading-tight truncate">
                {mode === 'create' ? 'Add New Company' : (company?.name ?? 'Company Details')}
              </SheetTitle>
              <SheetDescription className="text-xs">
                {mode === 'create' ? 'Fill in the details below to create a new company' : company?.code}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {mode === 'create' ? (
          <CreateCompanyForm
            existingCodes={existingCodes}
            onCreated={(created) => {
              onCompanyCreated?.(created);
              onOpenChange(false);
            }}
            onCancel={() => onOpenChange(false)}
            isDemo={isDemo}
          />
        ) : !company ? null : (
          <Tabs defaultValue="details" className="flex flex-col flex-1 min-h-0">
            <div className="px-6 pt-3 pb-0 border-b shrink-0">
              <TabsList className="h-9 w-full rounded-none bg-transparent p-0 gap-0 justify-start">
                <TabsTrigger
                  value="details"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                >
                  Details & Integrations
                </TabsTrigger>
                <TabsTrigger
                  value="users"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                >
                  Users with Access
                  {company.userCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[10px] font-medium h-4 min-w-4 px-1">
                      {company.userCount}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="details" className="flex-1 overflow-y-auto mt-0">
              <div className="px-6 py-5 space-y-6">
                <section>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Company Details
                  </h3>
                  <CompanyDetailsSection
                    company={company}
                    onUpdate={onCompanyUpdate}
                  />
                </section>

                <Separator />

                <section className="pb-6">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Integrations
                  </h3>
                  <div className="space-y-1">
                    <IntegrationSubSection
                      companyId={company.id}
                      type="hr"
                      product={company.hrIntegration.product}
                      externalInstanceId={company.hrIntegration.externalInstanceId}
                      status={company.hrIntegration.status}
                      lastSyncAt={company.hrIntegration.lastSyncAt}
                      onSaved={onCompanyUpdate}
                    />
                    <IntegrationSubSection
                      companyId={company.id}
                      type="project"
                      product={company.projectIntegration.product}
                      externalInstanceId={company.projectIntegration.externalInstanceId}
                      externalInstanceIdSecondary={company.projectIntegration.externalInstanceIdSecondary}
                      status={company.projectIntegration.status}
                      lastSyncAt={company.projectIntegration.lastSyncAt}
                      onSaved={onCompanyUpdate}
                    />
                    <IntegrationSubSection
                      companyId={company.id}
                      type="timesheet"
                      product={company.timesheetIntegration.product}
                      externalInstanceId={company.timesheetIntegration.externalInstanceId}
                      status={company.timesheetIntegration.status}
                      lastSyncAt={company.timesheetIntegration.lastSyncAt}
                      onSaved={onCompanyUpdate}
                    />
                  </div>
                </section>
              </div>
            </TabsContent>

            <TabsContent value="users" className="flex-1 overflow-y-auto mt-0">
              <div className="px-6 py-5 pb-8">
                <UsersWithAccessSection
                  company={company}
                  onUserCountChange={handleUserCountChange}
                />
              </div>
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}
